import { decodePeriodValue, encodePeriodValue } from '@components'

import { useCallback, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { PeriodMode } from '../../../enums/periodMode'
import { useLiveSearch } from '../../../hooks/routing/useLiveSearch'
import { useOrganizationFiscalYears } from '../../../hooks/useOrganizationFiscalYears'
import type { PeriodValue } from '../../../types/periodValue'
import { getDefaultReportPeriod } from '../utils/getDefaultReportPeriod'
import { replacePeriodSearchParam } from '../utils/replacePeriodSearchParam'

export interface UseExpertReportPeriodResult {
  encodedPeriod: string
  // Decoded period mode, exposed for analytics payloads so callers don't have to decode it themselves.
  periodType: PeriodMode | undefined
  // Returns false when the change was a no-op (a FiscalYear pick before the org id is available) so callers can
  // avoid side effects like clearing a search or syncing another store.
  setPeriod: (value: PeriodValue) => boolean
}

/**
 * Period shared across expert-report views (supplier/customer balance, P&L, trial balance, balance sheet), stored as
 * an encoded string in the `period` URL query param so it survives a refresh, is shareable, and carries across
 * navigation. Before the URL has a period, it falls back to the organization's current fiscal year.
 */
export const useExpertReportPeriod = (): UseExpertReportPeriodResult => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const getLiveSearch = useLiveSearch()
  const { organization } = useUserOrganization()
  const { defaultYear } = useOrganizationFiscalYears()

  const periodFromUrl = useMemo(() => new URLSearchParams(search).get('period') ?? '', [search])

  const defaultEncodedPeriod = useMemo(
    () => getDefaultReportPeriod({ fiscalYear: defaultYear, organizationId: organization?.id }),
    [defaultYear, organization?.id],
  )

  const encodedPeriod = periodFromUrl || defaultEncodedPeriod

  // Writes the default into the URL once the organization loads, so it's shareable/bookmarkable like an
  // explicitly picked period. Skipped once the URL already has one.
  useEffect(() => {
    if (!periodFromUrl && defaultEncodedPeriod) {
      replacePeriodSearchParam(navigate, getLiveSearch(), defaultEncodedPeriod)
    }
  }, [defaultEncodedPeriod, getLiveSearch, navigate, periodFromUrl])

  const setPeriod = useCallback(
    (value: PeriodValue): boolean => {
      // FiscalYear encoding needs the org id; skip until it loads to avoid persisting a malformed `fiscalyear:,<year>`.
      if (value.mode === PeriodMode.FiscalYear && !organization?.id) {
        return false
      }

      replacePeriodSearchParam(
        navigate,
        getLiveSearch(),
        encodePeriodValue(value, organization?.id ?? '') ?? defaultEncodedPeriod,
      )
      return true
    },
    [defaultEncodedPeriod, getLiveSearch, navigate, organization?.id],
  )

  return { encodedPeriod, periodType: decodePeriodValue(encodedPeriod)?.mode, setPeriod }
}
